/**
 * Access rights mirrored from the backend (`RoleAccessRights`, ADR-0016). The client uses them only
 * to show or hide functions; the server enforces every right on its own.
 */
export type AccessRight =
  | "AccessOwnRecords"
  | "RecordForOthers"
  | "ReadEmployeeTimes"
  | "ApproveCorrections"
  | "ManageViolations"
  | "ManageMasterData"
  | "ReadHealthData"
  | "ManageTenant"
  | "ExportAuthorityRecords"
  | "ExportPayroll"
  | "VerifyAuditLog"
  | "ReadAggregatedReports"
  | "ReadConfigurationExport"
  | "ManageDelegations"
  | "ManageProjects"
  | "DecideLegalDocuments"
  | "ReadSubscription"
  | "ManageBilling"
  | "ManageContacts"
  | "ExportTenantData"
  | "ApproveAbsences"
  | "ClockForOthers";

const grantsByRole: Readonly<Record<string, readonly AccessRight[]>> = {
  Employee: ["AccessOwnRecords"],
  Manager: [
    "AccessOwnRecords",
    "RecordForOthers",
    "ClockForOthers",
    "ReadEmployeeTimes",
    "ApproveCorrections",
    "ManageViolations",
    "ManageDelegations",
    "ManageProjects",
    "ApproveAbsences",
  ],
  HrAdmin: [
    "AccessOwnRecords",
    // Clock events on behalf of everyone employed on the day, not corrections (T-170).
    "ClockForOthers",
    "ReadEmployeeTimes",
    "ManageViolations",
    "ManageMasterData",
    "ReadHealthData",
    "ExportAuthorityRecords",
    "ExportPayroll",
    "ReadConfigurationExport",
    "ManageProjects",
    "ExportTenantData",
    "ApproveAbsences",
  ],
  TenantAdmin: [
    "AccessOwnRecords",
    "ManageTenant",
    "VerifyAuditLog",
    "ReadConfigurationExport",
    "DecideLegalDocuments",
    "ReadSubscription",
    "ManageContacts",
    "ExportTenantData",
  ],
  Auditor: ["ReadEmployeeTimes", "ExportAuthorityRecords", "VerifyAuditLog"],
  WorksCouncil: ["ReadAggregatedReports", "ReadConfigurationExport"],
  // No access to working time data, not even own records (T-134).
  BillingAdmin: ["ReadSubscription", "ManageBilling"],
};

/**
 * Returns whether any of the roles grants the access right. Unknown roles grant nothing.
 *
 * @param roles - Roles of the person (from `GET /me`).
 * @param accessRight - The requested access right.
 * @returns `true` if granted.
 */
export function hasAccessRight(roles: readonly string[], accessRight: AccessRight): boolean {
  const granted = roles.some((role) => {
    const grants = Object.hasOwn(grantsByRole, role) ? grantsByRole[role] : undefined;
    const includes = grants?.includes(accessRight) ?? false;

    return includes;
  });

  return granted;
}
